import { useEffect, useState } from "react";
import { Badge, Card, CardBody, CardHeader, Col, Nav, NavItem, NavLink, Row } from "reactstrap";
import './styles.css';

/**
 * Building blocks shared by every CRM record page (customer, lead, call, task,
 * meeting) so they all read the same way: a sticky identity rail on the left,
 * a KPI strip and stacked sections on the right.
 */

export function RecordLayout({ children }) {
  return <div className="crm-record-layout">{children}</div>;
}

export function RecordRail({ children }) {
  return <aside className="crm-record-rail">{children}</aside>;
}

export function RecordMain({ children }) {
  return <div className="crm-record-main">{children}</div>;
}

export function Avatar({ src, initials, fallback, onClick }) {
  const clickable = onClick ? ' is-clickable' : '';

  if (src) {
    return <img src={src} alt="" className={'crm-record-avatar' + clickable} onClick={onClick} />;
  }

  return (
    <div className={'crm-record-avatar-empty mx-auto' + clickable} onClick={onClick}>
      {initials || fallback}
    </div>
  );
}

/** A single icon + text line in the identity rail. Renders nothing without a value. */
export function Fact({ icon, value, href, to }) {
  if (!value) return null;

  return (
    <div className="d-flex align-items-center gap-2">
      <span className="text-muted d-flex flex-shrink-0">{icon}</span>
      {href ? (
        <a href={href} className="crm-record-value text-decoration-none">{value}</a>
      ) : (
        <span className="crm-record-value">{value}</span>
      )}
    </div>
  );
}

export function RecordNav({ sections, activeId, onSelect }) {
  return (
    <Card className="mt-3">
      <CardBody className="crm-record-nav p-2">
        {sections.map(({ id, label, icon }) => (
          <a
            key={id}
            href={`#${id}`}
            className={activeId === id ? 'active' : ''}
            onClick={(e) => {
              e.preventDefault();
              onSelect(id);
            }}
          >
            {icon}
            {label}
          </a>
        ))}
      </CardBody>
    </Card>
  );
}

export function RecordTabs({ tabs, activeTab, onChange }) {
  return (
    <Nav tabs className="crm-record-tabs mt-3">
      {tabs.map(tab => (
        <NavItem key={tab.id}>
          <NavLink active={activeTab === tab.id} onClick={() => onChange(tab.id)}>
            {tab.label}
          </NavLink>
        </NavItem>
      ))}
    </Nav>
  );
}

export function StatTile({ icon, background, color, value, label, count }) {
  return (
    <div className="d-flex align-items-center gap-3">
      <div className="crm-record-stat-icon" style={{ background, color }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="crm-record-stat-value text-truncate">{value ?? '-'}</div>
        <div className="crm-record-stat-label">
          {label}{count > 0 ? ` · ${count}` : ''}
        </div>
      </div>
    </div>
  );
}

/** KPI strip: one card holding a row of tiles, plus anything extra underneath. */
export function StatStrip({ tiles, children }) {
  return (
    <Card>
      <CardBody>
        <Row className="g-3">
          {tiles.map((tile, index) => (
            <Col xl={tiles?.length < 4 ? 4 : 3} md="6" key={index}>
              <StatTile {...tile} />
            </Col>
          ))}
        </Row>
        {children}
      </CardBody>
    </Card>
  );
}

/**
 * Pipeline stepper. `stages` is [{ value, label }], `current` the active value.
 * Everything up to and including the current stage is filled with `color`.
 */
export function Stages({ stages, current, color = 'var(--nazar-primary)' }) {
  const currentIndex = stages.findIndex(stage => stage.value === current);

  return (
    <div className="crm-record-stages">
      {stages.map((stage, index) => (
        <div
          key={stage.value}
          className={'crm-record-stage' + (index === currentIndex ? ' is-current' : '')}
          title={stage.label}
        >
          <div
            className="crm-record-stage-bar"
            style={index <= currentIndex ? { background: color } : undefined}
          />
          <div className="crm-record-stage-label">{stage.label}</div>
        </div>
      ))}
    </div>
  );
}

export function SectionCard({ id, icon, title, count, action, children }) {
  return (
    <Card className="mt-3" id={id}>
      <CardHeader className="bg-white d-flex align-items-center justify-content-between gap-2 py-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted d-flex">{icon}</span>
          <h6 className="mb-0 fw-bold">{title}</h6>
          {count > 0 && <Badge color="light">{count}</Badge>}
        </div>
        {action}
      </CardHeader>
      {children}
    </Card>
  );
}

export function Field({ label, value, href, children, lg = 4 }) {
  const fullWidth = Number(lg) === 12;

  return (
    <Col lg={lg} sm={fullWidth ? 12 : 6}>
      <div className="crm-record-label mb-1">{label}</div>
      {children ? (
        children
      ) : href && value ? (
        <a href={href} className="crm-record-value text-decoration-none">{value}</a>
      ) : (
        <div className="crm-record-value">{value || '-'}</div>
      )}
    </Col>
  );
}

export function EmptyState({ message }) {
  return <div className="crm-record-empty">{message}</div>;
}

export function Dot({ color }) {
  return <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />;
}

/**
 * Highlights the section currently in view inside the page scroll container and
 * exposes a smooth scroll to any of them, driving the rail's jump navigation.
 * `readyKey` should change once the record data has arrived so the observer
 * re-attaches to the sections that were rendered with it.
 */
export function useScrollSpy(sectionIds, containerRef, enabled, readyKey) {
  const [activeSection, setActiveSection] = useState(null);
  const idsKey = sectionIds.join(',');

  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { root: containerRef.current, rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    idsKey.split(',').forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [enabled, idsKey, readyKey, containerRef]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return { activeSection, scrollToSection };
}

/** Initials for an avatar placeholder, e.g. "Acme Corp" -> "AC". */
export function initialsOf(name) {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}
